import { createSelector } from '@reduxjs/toolkit';
import { cloneDeep, get, isEmpty, isNil, merge } from 'lodash';
import { useSelector } from 'react-redux';

import {
  clearAllOverrides,
  resetClientFeatureFlags,
  setOverride,
  surveyDebuggerSelector,
  updateClientFeatureFlag
} from '@redux/reducers/debugger';
import { RootState, useAppDispatch, useAppSelector } from '@redux/store';

import DebuggerService from '@services/debugger';
import {
  ClientSideRenderingFeatureFlag,
  normalizeSurveyEngineFeatureFlagsList
} from '@services/featureFlags';

import { isElement } from '@utils/domNodeHelpers';

//----------------------------------------------------------------------
// TYPES
//----------------------------------------------------------------------

export type SpecStats = typeof defaultStats;

interface ValidationScriptBlock {
  blockContent: string;
  blockType: string;
  field: string | null;
}

//----------------------------------------------------------------------
// CONFIG
//----------------------------------------------------------------------

export const SURVEY_DEBUGGER_STORAGE_KEY = 'survey_debugger';

const ASK_NOW_NODE_NAME = 'innovation-testing-page';

export const defaultStats = {
  componentsHidden: [] as Array<string>,
  componentsVisible: [] as Array<string>,
  conditionsHidden: [] as Array<string>,
  conditionsLogic: [] as Array<string>
};

//----------------------------------------------------------------------
// BOOTSTRAPPING
//----------------------------------------------------------------------

export function isSurveysRuntimeDebuggerEnabled() {
  return get(
    window,
    ['surveyData', 'featureFlags', '24cr4_MPC8530_surveys_runtime_debugger'],
    false
  );
}

export function bootstrapDebuggerOverrides() {
  const debuggerOverrides = localStorage.getItem(SURVEY_DEBUGGER_STORAGE_KEY);

  if (!isNil(debuggerOverrides) && isSurveysRuntimeDebuggerEnabled()) {
    const id = window.surveyData.specId || window.surveyData.surveyid;
    const newSurveyData = merge(window.surveyData, JSON.parse(debuggerOverrides)[id]);
    window.surveyData = newSurveyData;
  }
}

//----------------------------------------------------------------------
// PARSERS
//----------------------------------------------------------------------

/**
 * Extracts the alphabetic word from a string pattern like "100100-merlin-692".
 * Removes numbers and symbols, returning only the part likely to be  tenant nickname.
 */

export function extractCompanyNameFromSpecId(input: string | null) {
  if (isNil(input) || isEmpty(input)) {
    return null;
  }

  const parts = input.split('-');

  for (const part of parts) {
    if (/^[a-zA-Z]+$/.test(part)) {
      return part;
    }
  }

  return null;
}

export function parseCompanyName(specNode: Element) {
  const companyName = specNode.getAttribute('company-name');
  if (!isNil(companyName)) {
    return companyName;
  }

  // Some specs might not have a `company-name` attribute.
  const cookieID = specNode.getAttribute('cookie');
  if (!isNil(cookieID)) {
    return cookieID;
  }

  // Some specs might not have `cookie` attribute.
  const specID = specNode.getAttribute('id');
  const tenantNicknameFromId = extractCompanyNameFromSpecId(specID);
  if (!isNil(tenantNicknameFromId)) {
    return tenantNicknameFromId;
  }

  // client-cluster-name is tempting but not an option.
  // Sometimes, it'll be a match with a tenant nickname.
  // Sometimes, it'll be something like this:
  // client-cluster-name="cr-den-qa2-smiladminsuite23cr2-1"
  // client-cluster-name="cr-denqa2-max-release-branch-2"
  return null;
}

export function parseClusterURL(document: Document, specNode: Element) {
  const clusterUrl = specNode.getAttribute('cluster-url');

  if (!isNil(clusterUrl)) {
    return clusterUrl;
  }

  // Some instances might not have `cluster-url`.
  // We should fallback to `return-feed` and process this string.
  const returnFeed = document.getElementsByTagName('return-feed')[0];

  if (!isNil(returnFeed)) {
    const baseUrl = returnFeed.getAttribute('url')!;
    const url = new URL(baseUrl);
    const hostname = url.hostname.replace(/-be(?=\.[^/]+$)/, '');
    return `https://${hostname}`;
  }

  return null;
}

/**
 * Recursive Utility To Fetch Nodes With Conditions For Entire Component.
 */

export function getConditionCount(
  node: Element | ChildNode,
  withConditionHidden: string[],
  withConditionLogic: string[]
) {
  if (isElement(node) && node.hasAttribute('condition')) {
    if (node.getAttribute('condition') === 'false') {
      withConditionHidden.push(node.nodeName);
    } else {
      withConditionLogic.push(node.nodeName);
    }
  }

  const children = Array.from(node.childNodes ?? []);

  for (const child of children) {
    getConditionCount(child, withConditionHidden, withConditionLogic);
  }

  return { withConditionHidden, withConditionLogic };
}

/**
 * Gets Total Component Nodes Split By Hidden and Non-hidden Per Survey Page And Overall Survey.
 * Gets Total Nodes with Conditions Split By Hidden and Logic Per Survey Page And Overall Survey.
 */

export function parseStats(pageNodes: Element[]) {
  const allStats = cloneDeep(defaultStats);
  const paginatedStats: Record<number, SpecStats> = {};

  pageNodes.forEach((pageNode, index) => {
    const componentsHidden: string[] = [];
    const componentsVisible: string[] = [];
    const conditionsHidden: string[] = [];
    const conditionsLogic: string[] = [];

    const immediateChildElements = Array.from(pageNode.children).filter(
      (node) => node.nodeType === 1 && node.nodeName !== ASK_NOW_NODE_NAME
    );

    immediateChildElements.forEach((child) => {
      if (child.hasAttribute('condition')) {
        componentsHidden.push(child.nodeName);
      } else {
        componentsVisible.push(child.nodeName);
      }
      getConditionCount(child, conditionsHidden, conditionsLogic);
    });

    paginatedStats[index + 1] = {
      componentsHidden,
      componentsVisible,
      conditionsHidden,
      conditionsLogic
    };

    allStats.componentsHidden.push(...componentsHidden);
    allStats.componentsVisible.push(...componentsVisible);
    allStats.conditionsHidden.push(...conditionsHidden);
    allStats.conditionsLogic.push(...conditionsLogic);
  });

  return { paginatedStats, allStats };
}

/**
 *  Recursive Utility To Fetch All The Validation Blocks Added With Javascript For Entire Survey
 */

export function parseJSValidationConditionLogic(firstMatchingChildNode: Element | ChildNode) {
  const scriptRegex = /SCRIPT\s*\((['"])([\s\S]*?)\1\)/gi;
  const results: ValidationScriptBlock[] = [];

  const attributes = ['validation', 'condition'];

  for (const attr of attributes) {
    if (isElement(firstMatchingChildNode) && firstMatchingChildNode.hasAttribute(attr)) {
      const attrValue = firstMatchingChildNode.getAttribute(attr);

      if (isNil(attrValue)) {
        continue;
      }

      const fieldValue = firstMatchingChildNode.getAttribute('field');

      for (const scriptMatch of Array.from(attrValue.matchAll(scriptRegex))) {
        results.push({
          blockType: firstMatchingChildNode.nodeName,
          blockContent: scriptMatch[2],
          field: fieldValue
        });
      }
    }
  }

  const children = Array.from(firstMatchingChildNode.childNodes ?? []);

  for (const child of children) {
    results.push(...parseJSValidationConditionLogic(child));
  }

  return results;
}
//----------------------------------------------------------------------
// SELECTORS
//----------------------------------------------------------------------

const hasOverridesSelector = (state: RootState) =>
  !isEmpty(state.surveyDebugger.clientFeatureFlags.draft);

const clientFeatureFlagsSelector = createSelector(
  [(state: RootState) => state.surveyDebugger.clientFeatureFlags.final],
  (clientFeatureFlags) => {
    return Object.entries(clientFeatureFlags).filter(
      (entry) => entry[0] !== '24cr4_MPC8530_surveys_runtime_debugger'
    ) as Array<[ClientSideRenderingFeatureFlag, boolean]>;
  }
);

//----------------------------------------------------------------------
// HOOKS
//----------------------------------------------------------------------

export function useDebugger() {
  const { isRuntimeDebuggerEnabled, brandingStyleSheets, surveySpec, loading, error } =
    useSelector(surveyDebuggerSelector);

  return { isRuntimeDebuggerEnabled, brandingStyleSheets, surveySpec, loading, error };
}

export function useFeatureFlagsDebugger() {
  const dispatch = useAppDispatch();
  const clientFeatureFlags = useAppSelector(clientFeatureFlagsSelector);
  const hasOverrides = useAppSelector(hasOverridesSelector);
  const state = useAppSelector(surveyDebuggerSelector);

  // State

  const hasOverridesForAnySurvey = DebuggerService.hasOverridesForAnySurvey();

  const hasOverrideFor = (id: ClientSideRenderingFeatureFlag) => {
    return !isNil(state.clientFeatureFlags.draft[id]);
  };

  const hasScheduledOverrideFor = (id: ClientSideRenderingFeatureFlag) => {
    if (hasOverrideFor(id)) {
      return state.clientFeatureFlags.draft[id] !== state.clientFeatureFlags.prev[id];
    }
  };

  // Events

  const onChange = (id: ClientSideRenderingFeatureFlag, newValue: boolean) => {
    dispatch(updateClientFeatureFlag({ id, value: newValue }));
  };

  const onResetSurveyOverrides = () => {
    dispatch(resetClientFeatureFlags());
  };

  const onResetAllOverrides = () => {
    dispatch(clearAllOverrides());
  };

  const onSave = () => {
    const newEntry = { featureFlags: state.clientFeatureFlags.draft };
    dispatch(setOverride(newEntry));
  };

  // API

  return {
    // Events
    onChange,
    onSave,
    onResetSurveyOverrides,
    onResetAllOverrides,
    // Predicates
    hasOverrides,
    hasOverrideFor,
    hasScheduledOverrideFor,
    hasOverridesForAnySurvey,
    // Data
    serverFeatureFlags: normalizeSurveyEngineFeatureFlagsList(),
    clientFeatureFlags
  };
}
